import execa from "execa";
import { mkdirp, pathExists, readJson, writeFile } from "fs-extra";
import { homedir } from "os";
import { join } from "path";
import getNpmPackagesByKeyword from "./getNpmPackagesByKeyword";

const NPM_INTEGRATION_KEYWORD = "buurman-integration";
const NPM_TASK_KEYWORD = "buurman-task";
const NPM_FEATURE_DETECTOR_KEYWORD = "buurman-feature-detector";

const BUURMAN_HOME = join(homedir(), ".buurman");
const CACHE_PACKAGE = join(BUURMAN_HOME, "cache");
const CACHE_NODE_MODULES = join(CACHE_PACKAGE, "node_modules");
const CACHE_PACKAGE_JSON = join(CACHE_PACKAGE, "package.json");
const CACHE_GITIGNORE = join(CACHE_PACKAGE, ".gitignore");

const exec = (command: string, args: string[] = []) =>
    execa(command, args, { env: process.env, cwd: CACHE_PACKAGE });

const npm = (...args: string[]) => exec("npm", args);
npm.init = () => npm("init", "-y");
npm.update = () => npm("update");
npm.install = (...args: string[]) => npm("install", ...args);

const git = (...args: string[]) => exec("git", args);
git.init = () => git("init");
git.hasCleanWorkingCopy = async () =>
    !(await git("status", "--porcelain")).stdout;
git.add = (...paths: string[]) => git("add", ...paths);
git.commit = (commitMessage: string) => git("commit", "-m", commitMessage);
git.commitAll = async (commitMessage: string) => {
    await git.add(".");
    await git.commit(commitMessage);
};

const ensureLocalCachePackage = async () => {
    if (await pathExists(CACHE_PACKAGE_JSON)) {
        return;
    }

    await writeFile(CACHE_GITIGNORE, `node_modules/`);

    await npm.init();
    await git.init();
    await git.commitAll("initial commit");
};

const getCachedPackageNames = async () => {
    if (!(await pathExists(CACHE_PACKAGE_JSON))) {
        return [];
    }

    const { dependencies = {} } = await readJson(CACHE_PACKAGE_JSON);

    return Object.keys(dependencies);
};

const getCachedTaskPackageNames = async () => {
    const taskPackageNames: string[] = [];
    const packageJsonPath = (name: string) =>
        join(CACHE_NODE_MODULES, name, "package.json");

    for (const name of await getCachedPackageNames()) {
        const { keywords = [] } = await readJson(packageJsonPath(name));

        if (!keywords.includes(NPM_TASK_KEYWORD)) {
            continue;
        }

        taskPackageNames.push(name);
    }

    return taskPackageNames;
};

const getCachedIntegrationPackageNames = async () => {
    const integrationPackageNames: string[] = [];
    const packageJsonPath = (name: string) =>
        join(CACHE_NODE_MODULES, name, "package.json");

    for (const name of await getCachedPackageNames()) {
        const { keywords = [] } = await readJson(packageJsonPath(name));

        if (!keywords.includes(NPM_INTEGRATION_KEYWORD)) {
            continue;
        }

        integrationPackageNames.push(name);
    }

    return integrationPackageNames;
};

const getCachedFeatureDetectorPackageNames = async () => {
    const featureDetectorPackageNames: string[] = [];
    const packageJsonPath = (name: string) =>
        join(CACHE_NODE_MODULES, name, "package.json");

    for (const name of await getCachedPackageNames()) {
        const { keywords = [] } = await readJson(packageJsonPath(name));

        if (!keywords.includes(NPM_FEATURE_DETECTOR_KEYWORD)) {
            continue;
        }

        featureDetectorPackageNames.push(name);
    }

    return featureDetectorPackageNames;
};

const getTaskPackageNames = async () =>
    (await getNpmPackagesByKeyword(NPM_TASK_KEYWORD)).map(({ name }) => name);

const getIntegrationPackageNames = async () =>
    (await getNpmPackagesByKeyword(NPM_INTEGRATION_KEYWORD)).map(
        ({ name }) => name,
    );

const getFeatureDetectorPackageNames = async () =>
    (await getNpmPackagesByKeyword(NPM_FEATURE_DETECTOR_KEYWORD)).map(
        ({ name }) => name,
    );

const getMissingPackageNames = async () => {
    const cachedPackageNames = await getCachedPackageNames();
    const packageNames = [
        ...(await getTaskPackageNames()),
        ...(await getIntegrationPackageNames()),
        ...(await getFeatureDetectorPackageNames()),
    ];

    return packageNames.filter(v => !cachedPackageNames.includes(v));
};

const installMissingPackages = async () => {
    await ensureLocalCachePackage();
    const packageNames = await getMissingPackageNames();

    if (!packageNames.length) {
        return;
    }

    await npm.install("--save-exact", ...packageNames);

    const commitMessage = [
        "install tasks",
        "",
        ...packageNames.map(name => ` - ${name}`),
    ].join("\n");

    await git.commitAll(commitMessage);
};

const updateInstalledPackages = async () => {
    await ensureLocalCachePackage();
    const cachedPackages = await getCachedPackageNames();

    if (!cachedPackages.length) {
        return;
    }

    await npm.update();

    if (await git.hasCleanWorkingCopy()) {
        return;
    }

    await git.commitAll("update packages");
};

export const ensureAllPackagesAreInstalled = async () => {
    await mkdirp(CACHE_PACKAGE);

    try {
        await updateInstalledPackages();
        await installMissingPackages();
    } catch (err) {
        console.error(`Failed to ensure all packages are installed.`);
        console.error(err.message);
    }
};

// see https://github.com/rollup/rollup-plugin-commonjs/issues/396
const _require = new Function("return require;");

export const getTasks = async () =>
    (await getCachedTaskPackageNames()).map(
        name => _require(join(CACHE_NODE_MODULES, name)).default,
    );

export const getIntegrations = async () =>
    (await getCachedIntegrationPackageNames()).map(
        name => _require(join(CACHE_NODE_MODULES, name)).default,
    );

export const getFeatureDetectors = async () =>
    (await getCachedFeatureDetectorPackageNames()).map(
        name => _require(join(CACHE_NODE_MODULES, name)).default,
    );
